import React, { useEffect, useState } from "react";
import { Lead } from "../../api/lead";

interface LeadDetailsModalProps {
    lead: Lead | null;
    isOpen: boolean;
    onClose: () => void;
}

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({
    lead,
    isOpen,
    onClose,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (isOpen) {
            setPosition({ x: 0, y: 0 });
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest("button, a, .btn-close")) return;
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        });
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        }
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, dragStart]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleEscKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            onClose();
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("keydown", handleEscKey);
        }
        return () => {
            document.removeEventListener("keydown", handleEscKey);
        };
    }, [isOpen]);

    if (!isOpen || !lead) return null;

    return (
        <div
            className="modal fade show"
            style={{
                display: "block",
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.5)",
                zIndex: 1050,
                animation: "fadeIn 0.2s ease-in-out",
            }}
            onClick={handleBackdropClick}
        >
            <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .lead-details-modal .modal-header {
          cursor: move;
          user-select: none;
          background: white;
          border-bottom: 1px solid #dee2e6;
          padding: 1rem 1.5rem;
        }
        .lead-details-modal .modal-title {
          color: #dc3545;
          font-weight: 600;
        }
        .custom-btn-close {
          background: transparent;
          border: 1px solid #dc3545 !important;
          color: #dc3545 !important;
          font-size: 1.5rem;
          line-height: 1;
          opacity: 1;
          padding: 0;
          width: 32px;
          height: 32px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .custom-btn-close:hover {
          background: #dc3545 !important;
          color: white !important;
          opacity: 1;
        }
        .custom-btn-close i {
          font-size: 1.25rem;
        }
        .lead-details-modal .modal-content {
          border: none;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          will-change: transform;
        }
        .lead-details-modal .modal-dialog {
          transition: transform 0.05s ease-out;
        }
        .lead-details-modal .modal-body {
          padding: 1.5rem;
        }
        .lead-details-modal .modal-footer {
          border-top: 1px solid #e9ecef;
          padding: 1rem 1.5rem;
          background: #f8f9fa;
        }
        .info-section {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        .info-section h6 {
          color: #495057;
          font-weight: 600;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #667eea;
        }
        .info-row {
          display: flex;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e9ecef;
        }
        .info-row:last-child {
          border-bottom: none;
        }
        .info-label {
          font-weight: 600;
          color: #6c757d;
          min-width: 180px;
          flex-shrink: 0;
        }
        .info-value {
          color: #212529;
          word-break: break-word;
        }
      `}</style>

            <div
                className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable lead-details-modal"
                style={{
                    transform: `translate(${position.x}px, ${position.y}px)`,
                    pointerEvents: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header" onMouseDown={handleMouseDown}>
                        <h5 className="modal-title">
                            <i className="ti ti-eye me-2" />
                            Lead Details
                        </h5>
                        <button
                            type="button"
                            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <i className="ti ti-x" />
                        </button>
                    </div>

                    <div className="modal-body">
                        {/* Lead Information */}
                        <div className="info-section">
                            <h6>
                                <i className="bi bi-bullseye me-2" />
                                Lead Information
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Lead ID:</span>
                                <span className="info-value">{lead.lead_id || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Value:</span>
                                <span className="info-value">
                                    {lead.Currency} {lead.lead_value || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Status:</span>
                                <span className="info-value">
                                    <span className={`badge bg-info`}>
                                        {lead.lead_status || "N/A"}
                                    </span>
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Type:</span>
                                <span className="info-value">{lead.leadtype?.leadtype_name || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Source:</span>
                                <span className="info-value">{lead.leadsource?.LeadSource_name || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Stage:</span>
                                <span className="info-value">{lead.leadstage?.LeadStage_name || "N/A"}</span>
                            </div>
                        </div>

                        {/* Contact Information */}
                        {lead.contact && (
                            <div className="info-section">
                                <h6>
                                    <i className="ti ti-user me-2" />
                                    Contact Information
                                </h6>
                                <div className="info-row">
                                    <span className="info-label">Company:</span>
                                    <span className="info-value">{lead.contact.company_name || "N/A"}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Contact Person:</span>
                                    <span className="info-value">{lead.contact.contact_person_name || "N/A"}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Email:</span>
                                    <span className="info-value">{lead.contact.company_mail || "N/A"}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Phone:</span>
                                    <span className="info-value">{lead.contact.company_phone || "N/A"}</span>
                                </div>
                            </div>
                        )}

                        {/* Product Information */}
                        {lead.products && (
                            <div className="info-section">
                                <h6>
                                    <i className="ti ti-box me-2" />
                                    Product Details
                                </h6>
                                <div className="info-row">
                                    <span className="info-label">Product Name:</span>
                                    <span className="info-value">{lead.products.product_name || "N/A"}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Price:</span>
                                    <span className="info-value">{lead.Currency} {lead.products.product_price || "N/A"}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Logins:</span>
                                    <span className="info-value">{lead.no_logins || "N/A"}</span>
                                </div>
                            </div>
                        )}

                        {/* Owner */}
                        <div className="info-section">
                            <h6>
                                <i className="ti ti-user-check me-2" />
                                Lead Owner
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Name:</span>
                                <span className="info-value">{lead.users?.name || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Email:</span>
                                <span className="info-value">{lead.users?.email || "N/A"}</span>
                            </div>
                        </div>

                        {/* Created/Updated */}
                        {(lead.created_at || lead.updated_at) && (
                            <div className="text-center text-muted small mt-3">
                                {lead.created_at && (
                                    <div>
                                        <i className="ti ti-calendar me-1" />
                                        Created: {new Date(lead.created_at).toLocaleString()}
                                    </div>
                                )}
                                {lead.updated_at && (
                                    <div className="mt-1">
                                        <i className="ti ti-clock me-1" />
                                        Updated: {new Date(lead.updated_at).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light px-4"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeadDetailsModal;
